import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

const tiers = [
  {
    title: 'Mobile',
    price: 'Free',
    description: 'Start with the iOS and Android app.',
    features: ['iOS app access', 'Android app access', 'Indicators included', 'Basic support', "1 Trading Pair"],
    buttonText: 'Start free',
    buttonVariant: 'outlined',
  },
  {
    title: 'Web + Mobile Annual',
    price: '$100',
    cadence: '/year',
    description: 'Best value if you want the dashboard experience.',
    features: ['Web dashboard access', 'iOS + Android apps', 'Priority support', 'Ad Free Mobile', '10 Trading Pairs'],
    buttonText: 'Get annual access',
    buttonVariant: 'contained',
    featured: true,
  },
  {
    title: 'Web + Mobile Monthly',
    price: '$10',
    cadence: '/month',
    description: 'Flexible access to the full platform.',
    features: ['Web dashboard access', 'iOS + Android apps', 'Priority support', 'Ad Free Mobile', 'Cancel anytime', '10 Trading Pairs'],
    buttonText: 'Choose monthly',
    buttonVariant: 'outlined',
  },
];

export default function Pricing() {
  return (
    <Container id="pricing" sx={{ py: { xs: 8, sm: 12 } }}>
      <Stack spacing={2} sx={{ maxWidth: 680, mb: 4.5 }}>
        <Typography
          variant="caption"
          sx={{ color: 'primary.light', letterSpacing: 2, textTransform: 'uppercase' }}
        >
          Pricing
        </Typography>
        <Typography component="h2" variant="h2">
          Start free on mobile, unlock the full desk on web.
        </Typography>
        <Typography color="text.secondary">
          The mobile apps stay free. The subscription is for traders who want the
          full web dashboard, deeper monitoring, and a faster control surface.
        </Typography>
      </Stack>
      <Grid container spacing={2.5}>
        {tiers.map((tier) => (
          <Grid item xs={12} md={4} key={tier.title}>
            <Card
              variant="outlined"
              sx={{
                p: 3,
                height: '100%',
                borderRadius: '28px',
                borderColor: tier.featured ? 'primary.main' : 'divider',
                bgcolor: tier.featured ? 'rgba(21, 155, 238, 0.08)' : 'rgba(255,255,255,0.03)',
              }}
            >
              <Stack spacing={2.5} sx={{ height: '100%' }}>
                <Box>
                  {tier.featured ? (
                    <Chip
                      label="Most popular"
                      size="small"
                      sx={{
                        mb: 1.5,
                        borderRadius: '999px',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                      }}
                    />
                  ) : null}
                  <Typography variant="h5">{tier.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {tier.description}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h3" component="div">
                    {tier.price}
                    {tier.cadence ? (
                      <Typography component="span" variant="h6" color="text.secondary">
                        {tier.cadence}
                      </Typography>
                    ) : null}
                  </Typography>
                </Box>
                <Stack spacing={1.2} sx={{ flexGrow: 1 }}>
                  {tier.features.map((feature) => (
                    <Stack direction="row" spacing={1.25} alignItems="center" key={feature}>
                      <CheckCircleRoundedIcon sx={{ color: 'primary.light', fontSize: 20 }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  variant={tier.buttonVariant as 'outlined' | 'contained'}
                  component="a"
                  href="https://app.algo-trader.tech/signup"
                  target="_blank"
                  sx={{ borderRadius: '999px', py: 1.25 }}
                >
                  {tier.buttonText}
                </Button>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
